import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 30000, // 30 second timeout
});

// Helper function to extract error message from API response
export const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  if (!error.response?.data?.detail) {
    return error.message || defaultMessage;
  }
  
  const detail = error.response.data.detail;
  
  // If detail is a simple string, return it
  if (typeof detail === 'string') {
    return detail;
  }
  
  // If detail is an array (Pydantic validation errors)
  if (Array.isArray(detail)) {
    return detail.map(e => {
      if (typeof e === 'string') return e;
      return e.msg || e.message || JSON.stringify(e);
    }).join(', ');
  }
  
  // If detail is an object
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  
  return defaultMessage;
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor for error handling and session recovery
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle network errors
    if (!error.response) {
      console.error('Network error - checking connection');
      // Don't immediately logout on network issues
      return Promise.reject({
        ...error,
        message: 'Network error. Please check your internet connection.'
      });
    }
    
    // Handle 401 Unauthorized
    // 401, or a 403 'Not authenticated' (missing/invalid bearer), means the session needs recovery.
    // Other 403s (plan/permission denied) must NOT trigger a refresh or logout.
    const authStatus = error.response?.status;
    const isAuthFailure = authStatus === 401 || (authStatus === 403 && error.response?.data?.detail === 'Not authenticated');
    if (isAuthFailure && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Check if token exists but might be expired
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Try to refresh the session
          const refreshResponse = await axios.post(`${API}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (refreshResponse.data?.token) {
            localStorage.setItem('token', refreshResponse.data.token);
            originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, clear token and redirect
          console.error('Session expired, please login again');
          localStorage.removeItem('token');
          
          // Only redirect if not already on login page
          if (!window.location.pathname.includes('/login')) {
            window.dispatchEvent(new CustomEvent('session-expired'));
          }
        }
      }
    }
    
    // Handle 503 Service Unavailable with retry
    if (error.response?.status === 503 && !originalRequest._retryCount) {
      originalRequest._retryCount = 1;
      await new Promise(resolve => setTimeout(resolve, 2000));
      return api(originalRequest);
    }
    
    return Promise.reject(error);
  }
);

export default api;
